import type { GalleryMediaT } from '../../../../../type/galleryMediaT';

import { handleArrowKeyNavigation } from '@vs-shop/headless/useArrowKeyNavigation';
import {
  ProductGalleryLightbox,
  ProductGalleryLightboxClose,
  ProductGalleryLightboxPanel,
  ProductGalleryLightboxThumbTitle,
  ProductGalleryThumbBar,
  ProductGalleryThumbGrid,
  ProductGalleryVariant,
} from '@vs-shop/commerce-ui/productGallery';

import { getMemoizedIntlShapeWithDefaultLocale } from '../../../../../utils/intl';

import { GalleryModalMain } from './GalleryModalMain';
import { GalleryModalThumbItem } from './GalleryModalThumbItem';

type Props = {
  open: boolean;
  mediaSortedByPosition: GalleryMediaT[];
  selectedIdx: number;
  setSelectedIdx: (idx: number) => void;
  onClose: () => void;
  productName: string;
  alt: string;
};

/**
 * rcprofi's arrangement of the shared gallery lightbox: the imagery column, and
 * the thumb column beside it headed by the product's name.
 *
 * Thumbnails set the selected index and the carousel scrolls to it; the step
 * controls and a swipe move the carousel, and its own selection event sets the
 * index back. Which medium is current is stated once, as `aria-current`.
 */
export function GalleryModal({
  open,
  mediaSortedByPosition,
  selectedIdx,
  setSelectedIdx,
  onClose,
  productName,
  alt,
}: Props) {
  const intl = getMemoizedIntlShapeWithDefaultLocale();

  const dialogLabel = intl.formatMessage(
    {
      id: 'component.server.page.product.gallery.galleryModal.dialogLabel',
      defaultMessage: 'Galerie produktu {productName}',
    },
    { productName }
  );

  return (
    <ProductGalleryLightbox
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <ProductGalleryLightboxPanel
        aria-label={dialogLabel}
        // On the panel rather than on `window`: the dialog primitive stops
        // arrow keys at its popup, so a listener above it never sees them. The
        // overlay traps focus inside this panel, so every key a shopper presses
        // while it is open arrives here.
        onKeyDown={(event) => {
          handleArrowKeyNavigation(event.key, {
            selectedIdx,
            setSelectedIdx,
            maxIdx: mediaSortedByPosition.length - 1,
          });
        }}
      >
        <ProductGalleryLightboxClose
          aria-label={intl.formatMessage({
            id: 'component.server.page.product.gallery.galleryModal.closeModal',
            defaultMessage: 'Zavřít modal',
          })}
        />

        <GalleryModalMain
          media={mediaSortedByPosition}
          selectedIdx={selectedIdx}
          setSelectedIdx={setSelectedIdx}
          productName={productName}
          alt={alt}
        />

        <ProductGalleryThumbBar variant={ProductGalleryVariant.OVERLAY}>
          <ProductGalleryLightboxThumbTitle className="hidden lap:block">
            {productName}
          </ProductGalleryLightboxThumbTitle>

          <ProductGalleryThumbGrid variant={ProductGalleryVariant.OVERLAY}>
            {mediaSortedByPosition.map((media, idx) => (
              <GalleryModalThumbItem
                key={idx}
                media={media}
                selected={selectedIdx === idx}
                onClick={() => {
                  setSelectedIdx(idx);
                }}
                alt={alt}
              />
            ))}
          </ProductGalleryThumbGrid>
        </ProductGalleryThumbBar>
      </ProductGalleryLightboxPanel>
    </ProductGalleryLightbox>
  );
}
